import { useState, useEffect } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { warehouseAPI } from "../../services/api";
import { Plus, Edit2, Trash2, Save, X, Search } from "lucide-react";
import { useSubmit } from "../../hooks/useSubmit";
import { useEscClose } from "../../hooks/useEscClose";

// ── FormFields باید خارج از کامپوننت اصلی باشد تا re-render مشکل ایجاد نکند ──
interface FormFieldsProps {
  data: any;
  setData: any;
  dark: boolean;
  T: any;
  inp: React.CSSProperties;
  lbl: React.CSSProperties;
}

function FormFields({ data, setData, dark, T, inp, lbl }: FormFieldsProps) {
  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
      <div>
        <label style={lbl}>نام گدام *</label>
        <input value={data.name||""} onChange={e=>setData((f:any)=>({...f,name:e.target.value}))} placeholder="نام گدام" style={inp}/>
      </div>
      <div>
        <label style={lbl}>مسئول گدام</label>
        <input value={data.manager_name||""} onChange={e=>setData((f:any)=>({...f,manager_name:e.target.value}))} placeholder="نام مسئول" style={inp}/>
      </div>
      <div>
        <label style={lbl}>شماره تماس</label>
        <input value={data.phone||""} onChange={e=>setData((f:any)=>({...f,phone:e.target.value}))} placeholder="+93..." style={{...inp,direction:"ltr" as const}}/>
      </div>
      <div style={{gridColumn:"1/-1"}}>
        <label style={lbl}>موقعیت / آدرس</label>
        <input value={data.address||""} onChange={e=>setData((f:any)=>({...f,address:e.target.value}))} placeholder="آدرس گدام..." style={inp}/>
      </div>
      <div>
        <label style={lbl}>وضعیت</label>
        <select value={data.status||"active"} onChange={e=>setData((f:any)=>({...f,status:e.target.value}))} style={{...inp,background:dark?"#1e293b":"#f8faff"}}>
          <option value="active">فعال</option>
          <option value="inactive">غیرفعال</option>
        </select>
      </div>
      <div style={{gridColumn:"2/4"}}>
        <label style={lbl}>یادداشت</label>
        <input value={data.notes||""} onChange={e=>setData((f:any)=>({...f,notes:e.target.value}))} placeholder="..." style={inp}/>
      </div>
    </div>
  );
}

export default function WarehousesPage() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [search, setSearch]         = useState("");
  const [showAdd, setShowAdd]       = useState(false);
  const [editing, setEditing]       = useState<any>(null);
  const [toast, setToast]           = useState<{msg:string;ok:boolean}|null>(null);
  const [form, setForm] = useState({ name:"", address:"", manager_name:"", phone:"", notes:"", status:"active" });
  const { submitting, submit } = useSubmit();
  useEscClose(() => { setShowAdd(false); setEditing(null); });

  const showToast = (msg:string,ok=true)=>{ setToast({msg,ok}); setTimeout(()=>setToast(null),3000); };
  const load = async()=>{ const r = await warehouseAPI.warehousesList(); if(r.success) setWarehouses(r.warehouses); };
  useEffect(()=>{ load(); },[]);

  const inp: React.CSSProperties = { width:"100%", padding:"9px 12px", background:dark?"rgba(255,255,255,0.05)":"#f8faff", border:`1.5px solid ${dark?"rgba(255,255,255,0.08)":"rgba(0,0,0,0.08)"}`, borderRadius:8, color:T.text, fontSize:13, fontFamily:"'Vazirmatn','Segoe UI',sans-serif", direction:"rtl" as const, outline:"none", boxSizing:"border-box" as const };
  const lbl: React.CSSProperties = { fontSize:11, fontWeight:700, color:T.subtext, display:"block", marginBottom:4 };
  const card: React.CSSProperties = { borderRadius:14, padding:20, background:dark?"rgba(255,255,255,0.03)":"#fff", border:`1px solid ${T.cardBorder}`, marginBottom:14 };

  const filtered = warehouses.filter(w => !search || w.name.includes(search) || w.code.includes(search) || (w.manager_name||"").includes(search));

  const handleAdd = async()=>{
    if(!form.name.trim()){showToast("نام گدام الزامی است",false);return;}
    const r = await warehouseAPI.warehouseAdd(form);
    if(r.success){showToast(`✅ گدام ${r.code} ثبت شد`);setShowAdd(false);setForm({name:"",address:"",manager_name:"",phone:"",notes:"",status:"active"});load();}
    else showToast(r.message,false);
  };

  const handleUpdate = async()=>{
    const r = await warehouseAPI.warehouseUpdate(editing);
    if(r.success){showToast("✅ ویرایش شد");setEditing(null);load();}
    else showToast(r.message,false);
  };

  const handleDelete = async(id:number,name:string)=>{
    if(!window.confirm(`گدام "${name}" حذف شود؟`))return;
    const r = await warehouseAPI.warehouseDelete(id);
    if(r.success){showToast("✅ حذف شد");load();}
    else showToast(r.message,false);
  };

  return (
    <div dir="rtl">
      {toast&&<div style={{position:"fixed",top:20,left:"50%",transform:"translateX(-50%)",zIndex:9999,padding:"12px 24px",borderRadius:12,background:dark?"#1e293b":"#fff",border:`2px solid ${toast.ok?"#10b981":"#ef4444"}`,boxShadow:"0 8px 32px rgba(0,0,0,0.25)",color:toast.ok?"#10b981":"#ef4444",fontSize:13,fontWeight:700,fontFamily:"inherit"}}>{toast.msg}</div>}

      {/* فرم افزودن */}
      {showAdd&&(
        <div style={{...card,borderRight:"4px solid #8b5cf6",marginBottom:20}}>
          <div style={{fontSize:14,fontWeight:800,color:T.text,marginBottom:16}}>🏭 گدام جدید</div>
          <FormFields data={form} setData={setForm} dark={dark} T={T} inp={inp} lbl={lbl}/>
          <div style={{display:"flex",gap:8,marginTop:14,justifyContent:"flex-end"}}>
            <button onClick={()=>setShowAdd(false)} style={{display:"flex",alignItems:"center",gap:6,padding:"9px 16px",borderRadius:9,border:`1px solid ${T.cardBorder}`,background:"transparent",color:T.text,cursor:"pointer",fontSize:13,fontFamily:"inherit"}}><X size={13}/>انصراف</button>
            <button onClick={()=>submit(handleAdd)} disabled={submitting} style={{display:"flex",alignItems:"center",gap:6,padding:"9px 18px",borderRadius:9,border:"none",background:submitting?"#94a3b8":"linear-gradient(135deg,#8b5cf6,#7c3aed)",color:"white",cursor:submitting?"not-allowed":"pointer",fontSize:13,fontFamily:"inherit",fontWeight:700}}><Save size={13}/>{submitting?"در حال ثبت...":"ثبت گدام"}</button>
          </div>
        </div>
      )}

      {/* نوار جستجو */}
      <div style={{display:"flex",gap:10,marginBottom:18,alignItems:"center"}}>
        <div style={{flex:1,position:"relative"}}>
          <Search size={13} style={{position:"absolute",right:12,top:"50%",transform:"translateY(-50%)",color:T.subtext}}/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="جستجو در نام، کد، مسئول..." style={{...inp,paddingRight:34}}/>
        </div>
        {!showAdd&&<button onClick={()=>setShowAdd(true)} style={{display:"flex",alignItems:"center",gap:6,padding:"9px 18px",borderRadius:9,border:"none",background:"linear-gradient(135deg,#8b5cf6,#7c3aed)",color:"white",cursor:"pointer",fontSize:13,fontFamily:"inherit",fontWeight:700,whiteSpace:"nowrap"}}><Plus size={14}/>گدام جدید</button>}
      </div>

      {/* لیست */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))",gap:14}}>
        {filtered.length===0
          ?<div style={{gridColumn:"1/-1",textAlign:"center",padding:60,color:T.subtext}}><div style={{fontSize:40,marginBottom:10}}>🏭</div>هیچ گدامی ثبت نشده</div>
          :filtered.map(w=>(
            <div key={w.id} style={{...card,marginBottom:0,borderRight:`4px solid ${w.status==="active"?"#8b5cf6":"#64748b"}`}}>
              {editing?.id===w.id?(
                <>
                  <FormFields data={editing} setData={setEditing} dark={dark} T={T} inp={inp} lbl={lbl}/>
                  <div style={{display:"flex",gap:8,marginTop:12,justifyContent:"flex-end"}}>
                    <button onClick={()=>setEditing(null)} style={{display:"flex",alignItems:"center",gap:5,padding:"7px 14px",borderRadius:8,border:`1px solid ${T.cardBorder}`,background:"transparent",color:T.text,cursor:"pointer",fontSize:12,fontFamily:"inherit"}}><X size={12}/>انصراف</button>
                    <button onClick={()=>submit(handleUpdate)} disabled={submitting} style={{display:"flex",alignItems:"center",gap:5,padding:"7px 14px",borderRadius:8,border:"none",background:submitting?"#94a3b8":"linear-gradient(135deg,#10b981,#059669)",color:"white",cursor:submitting?"not-allowed":"pointer",fontSize:12,fontFamily:"inherit",fontWeight:700}}><Save size={12}/>{submitting?"...":"ذخیره"}</button>
                  </div>
                </>
              ):(
                <>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{width:42,height:42,borderRadius:12,background:"linear-gradient(135deg,#8b5cf6,#7c3aed)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>🏭</div>
                      <div>
                        <div style={{fontSize:14,fontWeight:800,color:T.text}}>{w.name}</div>
                        <div style={{fontSize:11,color:"#8b5cf6",fontWeight:700}}>{w.code}</div>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:5}}>
                      <button onClick={()=>setEditing({...w})} style={{width:28,height:28,borderRadius:7,border:"none",cursor:"pointer",background:"rgba(59,130,246,0.12)",color:"#3b82f6",display:"flex",alignItems:"center",justifyContent:"center"}}><Edit2 size={12}/></button>
                      <button onClick={()=>handleDelete(w.id,w.name)} style={{width:28,height:28,borderRadius:7,border:"none",cursor:"pointer",background:"rgba(239,68,68,0.12)",color:"#ef4444",display:"flex",alignItems:"center",justifyContent:"center"}}><Trash2 size={12}/></button>
                    </div>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:12}}>
                    {w.manager_name&&<div style={{color:T.subtext}}>👤 {w.manager_name}</div>}
                    {w.phone&&<div style={{color:T.subtext,direction:"ltr" as const}}>📞 {w.phone}</div>}
                    {w.address&&<div style={{color:T.subtext,gridColumn:"1/-1"}}>📍 {w.address}</div>}
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:12,paddingTop:12,borderTop:`1px solid ${T.cardBorder}`}}>
                    <span style={{fontSize:11,color:w.status==="active"?"#10b981":"#94a3b8",fontWeight:700}}>{w.status==="active"?"● فعال":"○ غیرفعال"}</span>
                    <span style={{fontSize:11,color:T.subtext}}>{w.item_count} نوع جنس</span>
                  </div>
                </>
              )}
            </div>
          ))
        }
      </div>
    </div>
  );
}